import { useEffect, useState } from 'react';
import { useStore } from './state/store';
import Landing from './components/Landing';
import Library from './components/Library';
import Messages from './components/Messages';
import ErrorBoundary from './components/ErrorBoundary';
import NowBar from './components/NowBar';
import AlertBar from './components/AlertBar';
import Shortcuts from './components/Shortcuts';

/**
 * Deliberate failure, for checking that the error boundaries actually contain a
 * broken view rather than blanking the board.
 *
 * Reached with ?boom=<view>, e.g. ?boom=timeline. An error boundary that has
 * never been fired is an assumption, not a safety net, and this is the only way
 * to fire one without shipping a real bug.
 */
function Boom({ area }: { area: string }): never {
  throw new Error(`Deliberate failure in ${area} (?boom= test hook)`);
}
import Header from './components/Header';
import SessionPanel from './components/SessionPanel';
import PresenterEditor from './components/PresenterEditor';
import A2Board from './views/A2Board';
import A1View from './views/A1View';
import Timeline from './views/Timeline';
import StageManager from './views/StageManager';
import Cues from './views/Cues';

export default function App() {
  const identity = useStore(s => s.identity);
  const state = useStore(s => s.state);
  const view = useStore(s => s.view);
  const toast = useStore(s => s.toast);
  const connect = useStore(s => s.connect);
  const connected = useStore(s => s.connected);

  const user = useStore(s => s.user);
  const authChecked = useStore(s => s.authChecked);
  const refreshAuth = useStore(s => s.refreshAuth);

  const [sessionPanel, setSessionPanel] = useState(false);
  const [editingPresenter, setEditingPresenter] = useState<string | null>(null);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  /** Count of messages seen while the panel was closed, for the header badge. */
  const [seenCount, setSeenCount] = useState(0);
  const messageCount = useStore(s => s.state?.messages.length ?? 0);

  useEffect(() => {
    if (messagesOpen) setSeenCount(messageCount);
  }, [messagesOpen, messageCount]);

  useEffect(() => { void refreshAuth(); }, [refreshAuth]);
  useEffect(() => { if (identity) connect(); }, [identity, connect]);

  // Wait for the first /auth/me before deciding, or a signed-in producer sees
  // the login screen flash on every reload.
  /*
   * The toast has to render on every screen, not just inside a show.
   * It used to live below the early returns, so a wrong password on the sign-in
   * form and a wrong code on the join form produced no feedback at all -- the
   * message was set in the store and never displayed.
   */
  const toastEl = toast
    ? <div className={`toast ${toast.kind}`} role="status">{toast.text}</div>
    : null;

  if (!authChecked) return <div className="empty">Loading…</div>;

  // Crew arrive with a participant identity and no account; producers arrive
  // signed in and pick a show first.
  if (!identity) {
    return (
      <>
        {user ? <Library /> : <Landing />}
        {toastEl}
      </>
    );
  }

  return (
    <div className="app">
      <Header
        onOpenSession={() => setSessionPanel(true)}
        onOpenMessages={() => setMessagesOpen(v => !v)}
        onOpenShortcuts={() => setShortcutsOpen(true)}
        unread={Math.max(0, messageCount - seenCount)}
      />

      <ErrorBoundary area="The show position bar">
        <NowBar />
      </ErrorBoundary>

      {/* Above the view, not inside it: a dying pack and an urgent message
          are equally useless on a tab nobody is looking at. */}
      <ErrorBoundary area="The alert bar">
        <AlertBar />
      </ErrorBoundary>

      {/* A disconnected board looks identical to a live one, so say so loudly.
          Everything on screen is the last state we heard, not necessarily now. */}
      {!connected && state && (
        <div className="offline-bar" role="status">
          <strong>Disconnected.</strong>
          <span>Showing the last known board — it may be out of date. Reconnecting…</span>
        </div>
      )}

      {!state ? (
        <div className="empty">Loading show…</div>
      ) : (
        <main className="work">
          {/* Keyed on the view so switching away clears a failed one. */}
          <ErrorBoundary key={view} area={`The ${view.toUpperCase()} view`}>
            {new URLSearchParams(location.search).get('boom') === view && <Boom area={view} />}
            {view === 'a2' && <A2Board onEditPresenter={setEditingPresenter} />}
            {view === 'a1' && <A1View />}
            {view === 'timeline' && <Timeline />}
            {view === 'stage' && <StageManager />}
            {view === 'cues' && <Cues />}
          </ErrorBoundary>
        </main>
      )}

      {shortcutsOpen && <Shortcuts onClose={() => setShortcutsOpen(false)} />}
      {messagesOpen && (
        <ErrorBoundary area="Messages">
          <Messages onClose={() => setMessagesOpen(false)} />
        </ErrorBoundary>
      )}
      {sessionPanel && (
        <ErrorBoundary area="The session panel">
          <SessionPanel onClose={() => setSessionPanel(false)} />
        </ErrorBoundary>
      )}
      {editingPresenter && (
        <ErrorBoundary area="The presenter editor">
        <PresenterEditor
          presenterId={editingPresenter}
          onClose={() => setEditingPresenter(null)}
          onNavigate={setEditingPresenter}
        />
        </ErrorBoundary>
      )}

      {toastEl}
    </div>
  );
}
